import { useQuery } from '@tanstack/react-query'

import { useOrg } from '@/admin/hooks/use-org'
import api from '@/admin/lib/api'

export interface AddressMatch {
  consumer_uuid: string
  name: string
  email: string
  /** Set when a transfer links the two customers. */
  relation: '' | 'previous_owner' | 'new_owner'
  /** Up to three: those filed at this address first, then the newest. */
  registrations: { uuid: string }[]
  /** Every live registration they hold in this tenant. */
  registration_total: number
}

export interface AddressMatches {
  total: number
  matches: AddressMatch[]
}

const NO_MATCHES: AddressMatches = { total: 0, matches: [] }

/**
 * Other customers of this tenant at the address a customer has: the copy filed
 * on ``holdingUuid`` when given, else their current address.
 */
export function useCustomerAddressMatches(
  consumerUuid: string | undefined,
  holdingUuid?: string,
) {
  // Internal staff only - it names other customers, so the endpoint refuses a
  // partner session and there is no call to make for one.
  const { isPartnerAccess } = useOrg()
  return useQuery<AddressMatches>({
    queryKey: ['customer-address-matches', consumerUuid ?? '', holdingUuid ?? ''],
    queryFn: () =>
      api
        .get<AddressMatches>(`/registrations/consumers/${consumerUuid}/address-matches/`, {
          params: holdingUuid ? { holding_uuid: holdingUuid } : {},
        })
        .then((r) => r.data ?? NO_MATCHES),
    enabled: !!consumerUuid && !isPartnerAccess,
    retry: false,
  })
}

/** Other customers of this tenant at a draft ``address`` being entered for one. */
export function useDraftAddressMatches(
  consumerUuid: string | undefined,
  address: Record<string, unknown> | null,
) {
  const { isPartnerAccess } = useOrg()
  return useQuery<AddressMatches>({
    queryKey: ['customer-address-check', consumerUuid ?? '', JSON.stringify(address ?? {})],
    queryFn: () =>
      api
        .post<AddressMatches>(`/registrations/consumers/${consumerUuid}/address-matches/`, {
          address,
        })
        .then((r) => r.data ?? NO_MATCHES),
    enabled: !!consumerUuid && !!address && !isPartnerAccess,
    retry: false,
  })
}
